// ads.js — Mobile: @capacitor-community/admob | Web: AdSense
import { Capacitor } from "@capacitor/core";
import { AdMob, BannerAdSize, BannerAdPosition } from "@capacitor-community/admob";

/* ===================== CONFIG ===================== */
// Native AdMob (Android/iOS)
const ADMOB = {
    BANNER_ID: "ca-app-pub-3366036914417048/6908177408",
};

// Web AdSense (browser)
const ADSENSE = {
    PUBLISHER_ID: "ca-pub-3366036914417048",
    SLOTS: {
        // Web Mobile
        CENTER: "8724564217",
        BOTTOM: "8993648216",

        // Web Desktop
        HORIZONTAL: "9484602808", // wide top/strip
        VERTICAL: "5245974894",   // right-rail 300x600-ish
        SQUARE: "7959219409",     // 300x250 / 336x280
    },
};

// Spacing reserved when a bottom banner is visible (mobile web / native)
const VISIBLE_MARGIN = 60; // px

// Web containers (keep consistent in your HTML)
const WEB_IDS = {
    bottom: "web-ad-banner-bottom",          // mobile bottom
    center: "web-ad-banner-center",          // mobile center
    horizontal: "web-ad-banner-horizontal",// horizontal horizontal strip
    vertical: "web-ad-banner-vertical",      // desktop right rail
    square: "web-ad-banner-square",          // desktop square box
};

/* ===================== STATE ===================== */
const IS_NATIVE = Capacitor.isNativePlatform();
let _bottomResizeStop = null; // web bottom ResizeObserver cleanup
let _adErrInstalled = false;

/* ===================== UTILS ===================== */
function setMargin(px) {
    const val = typeof px === "number" ? `${px}px` : px || "0px";
    document.documentElement.style.setProperty("--add-mob-margin", val);
}

/** Create a container by id if missing */
function ensureContainer(id) {
    return document.getElementById(id);
}

/* ===================== WEB HELPERS ===================== */
function installAdResourceErrorCatcher() {
    if (_adErrInstalled) return;
    _adErrInstalled = true;

    // Catch network resource errors from ad networks; remove ad & margin
    window.addEventListener(
        "error",
        (e) => {
            const t = e?.target;
            const src = (t && (t.src || t.href)) || "";
            if (src.includes("googlesyndication.com") || src.includes("doubleclick.net")) {
                console.warn("[ads] Ad resource error detected; removing web ads.");
                try { removeWebAd(); } catch {console.log('')}
            }
        },
        true
    );
}

/** Returns true only if there's meaningful publisher content on screen */
function hasPublisherContent() {
    // Prefer an explicit marker you control
    const marked = document.querySelectorAll('[data-publisher-content="true"]');
    if (marked.length > 0) {
        // ensure at least ~150 visible words across all marked nodes
        let words = 0;
        marked.forEach(n => {
            const txt = (n.innerText || n.textContent || "").trim();
            words += txt.split(/\s+/).filter(Boolean).length;
        });
        return words >= 150;
    }

    // Fallback: check main content area
    const main =
        document.querySelector('main') ||
        document.getElementById('app') ||
        document.body;

    const txt = (main.innerText || main.textContent || "").trim();
    const wordCount = txt.split(/\s+/).filter(Boolean).length;

    // Require a minimum word count so list-only / loader screens don't qualify
    return wordCount >= 200;
}

/** Push a new AdSense ad request; safe-guarded */
function requestAdSenseFill() {
    try {
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});
    } catch (err) {
        console.warn("[ads] AdSense push failed (likely blocked by extensions):", err);
    }
}

/** Generic AdSense renderer */
function renderAdSense(containerId, slotId, styleCss = "display:block") {
    const host = ensureContainer(containerId);
    if(!host) return null;
    host.style.cssText = "width:100%;display:block;";
    host.innerHTML = `
    <ins class="adsbygoogle"
         style="${styleCss}"
         data-ad-client="${ADSENSE.PUBLISHER_ID}"
         data-ad-slot="${slotId}"
         data-ad-format="fluid"
         data-full-width-responsive="true"></ins>`;
    requestAdSenseFill();
    return host;
}

/* ===== Specific Web Renderers ===== */
// Mobile
function renderWebCenter() {
    // No bottom margin
    if (typeof _bottomResizeStop === "function") _bottomResizeStop();
    _bottomResizeStop = null;
    setMargin(0);

    renderAdSense(WEB_IDS.center, ADSENSE.SLOTS.CENTER, "display:block;height:60px;");
}

function renderWebBottom() {

    // cleanup any old bottom observer
    if (typeof _bottomResizeStop === "function") _bottomResizeStop();
    _bottomResizeStop = null;

    const host = renderAdSense(WEB_IDS.bottom, ADSENSE.SLOTS.BOTTOM, "display:block;height:60px;");

    // Reserve space immediately; auto-tune to real height
    setMargin(VISIBLE_MARGIN);

    try {
        const ro = new ResizeObserver(() => {
            const h = Math.round(host.getBoundingClientRect().height || 0);
            setMargin(h > 0 ? h : VISIBLE_MARGIN);
        });
        ro.observe(host);
        _bottomResizeStop = () => ro.disconnect();
    } catch {
        console.warn("[ads] ResizeObserver not available; using fallback margin.");
    }

    // Optional: remove if never fills
    // addAdFillGuard(host, {
    //   timeoutMs: 6000,
    //   minHeight: 20,
    //   onFail: () => { try { host.innerHTML = ""; } catch {console.log('')} setMargin(0); },
    // });
}

// Desktop
function renderWebHorizontal() {
    setMargin(0);
    renderAdSense(WEB_IDS.horizontal, ADSENSE.SLOTS.HORIZONTAL, "display:block;max-height:90px;");
}

function renderWebVertical() {
    setMargin(0);
    renderAdSense(WEB_IDS.vertical, ADSENSE.SLOTS.VERTICAL, "display:block;max-width:300px;min-height:600px;margin:0 auto;");
}

function renderWebSquare() {
    setMargin(0);
    renderAdSense(WEB_IDS.square, ADSENSE.SLOTS.SQUARE, "display:block;max-width:336px;min-height:280px;margin:0 auto;");
}

/** Clear all web ad containers (keeps the nodes, just empties them) */
function removeWebAd() {
    try {
        if (typeof _bottomResizeStop === "function") _bottomResizeStop();
        _bottomResizeStop = null;

        [WEB_IDS.bottom, WEB_IDS.center, WEB_IDS.horizontal, WEB_IDS.vertical, WEB_IDS.square].forEach((id) => {
            const el = document.getElementById(id);
            if (el) {
                el.style.cssText = "";
                el.innerHTML = ""; // do not remove element, just clear
            }
        });
    } catch (err) {
        console.error("[ads] Web ad cleanup error:", err);
    }
    setMargin(0);
}

function ensureAdSenseScript() {
    if (!ADSENSE.PUBLISHER_ID) return;
    try {
        // installAdResourceErrorCatcher();
        const s = document.createElement("script");
        s.async = true;
        s.setAttribute("data-adsbygoogle", "true");
        s.crossOrigin = "anonymous";
        s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE.PUBLISHER_ID}`;
        document.head.appendChild(s);
    } catch (err) {
        console.error("[ads] Failed to append AdSense script:", err);
    }
}

/* ===================== PUBLIC API ===================== */
export async function initAds() {
    try {
        if (IS_NATIVE) {
            await AdMob.initialize({
                requestTrackingAuthorization: true,
                initializeForTesting: true,
            });
        } else {
            ensureAdSenseScript();
        }
    } catch (err) {
        console.error("[ads] Ad init failed:", err);
    } finally {
        setMargin(0);
    }
}

/**
 * Show banner
 * place:
 *  - MOBILE: "CENTER" | "BOTTOM"
 *  - DESKTOP: "HORIZONTAL" | "VERTICAL" | "SQUARE"
 */
export async function showBanner(opts = {}) {
    const place = (opts.place || "CENTER").toUpperCase();
    const bigScreen = window.innerWidth >= 1000;

    // Always clean up existing DOM/observers first on web
    if (!IS_NATIVE) removeWebAd();

    if (IS_NATIVE) {
        const position =
            place === "CENTER" ? BannerAdPosition.CENTER : BannerAdPosition.BOTTOM_CENTER;
        const adSize =
            position === BannerAdPosition.CENTER ? BannerAdSize.BANNER : BannerAdSize.ADAPTIVE_BANNER;

        try {
            try { await AdMob.removeBanner(); } catch {console.log('')}
            await AdMob.showBanner({ adId: ADMOB.BANNER_ID, adSize, position });
            setMargin(VISIBLE_MARGIN);
        } catch (err) {
            console.error("[ads] Native showBanner failed:", err);
            setMargin(0);
        }
        return;
    }

    // WEB
    try {

        // Block AdSense if the page doesn't have enough content
        // if (!hasPublisherContent()) {
        //     removeWebAd();
        //     setMargin(0);
        //     return; // do not render any web ad
        // }

        if (bigScreen) {
            if (place === "HORIZONTAL") return void renderWebHorizontal();
            if (place === "VERTICAL")   return void renderWebVertical();
            if (place === "SQUARE")     return void renderWebSquare();
            // default desktop
            return void renderWebHorizontal();
        }

        if (place === "CENTER") return void renderWebCenter();
        if (place === "BOTTOM") return void renderWebBottom();
        // default mobile
        renderWebBottom();
    } catch (err) {
        console.error("[ads] Web showBanner failed:", err);
        setMargin(0);
    }
}

/** Move an already-shown banner */
export async function moveBanner(place = "CENTER") {
    const target = place.toUpperCase();
    const bigScreen = window.innerWidth >= 1000;

    // Clean web DOM/observer each move
    if (!IS_NATIVE) removeWebAd();

    if (IS_NATIVE) {
        const position =
            target === "CENTER" ? BannerAdPosition.CENTER : BannerAdPosition.BOTTOM_CENTER;
        const adSize =
            position === BannerAdPosition.CENTER ? BannerAdSize.BANNER : BannerAdSize.ADAPTIVE_BANNER;

        try {
            try { await AdMob.removeBanner(); } catch {console.log('')}
            await AdMob.showBanner({ adId: ADMOB.BANNER_ID, adSize, position });
            setMargin(VISIBLE_MARGIN);
        } catch (err) {
            console.error("[ads] Native moveBanner failed:", err);
            setMargin(0);
        }
        return;
    }

    // WEB
    try {
        // Block AdSense if the page doesn't have enough content
        // if (!hasPublisherContent()) {
        //     removeWebAd();
        //     setMargin(0);
        //     return; // do not render any web ad
        // }
        if (bigScreen) {
            if (target === "HORIZONTAL") return void renderWebHorizontal();
            if (target === "VERTICAL")   return void renderWebVertical();
            if (target === "SQUARE")     return void renderWebSquare();
            return void renderWebHorizontal();
        }

        if (target === "CENTER") return void renderWebCenter();
        if (target === "BOTTOM") return void renderWebBottom();
    } catch (err) {
        console.error("[ads] Web moveBanner failed:", err);
        setMargin(0);
    }
}

/** Hide banner (keeps it resumable) */
export async function hideBanner() {
    if (IS_NATIVE) {
        try {
            await AdMob.hideBanner();
        } catch (err) {
            console.error("[ads] Native hideBanner failed:", err);
        }
        setMargin(0);
        return;
    }

    try {
        [WEB_IDS.bottom, WEB_IDS.center, WEB_IDS.horizontal, WEB_IDS.vertical, WEB_IDS.square].forEach(
            (id) => {
                const el = document.getElementById(id);
                if (el) el.style.display = "none";
            }
        );
        setMargin(0);
    } catch (err) {
        console.error("[ads] Web hideBanner failed:", err);
    }
}

/** Resume banner (shows previously hidden web containers) */
export async function resumeBanner() {
    if (IS_NATIVE) {
        try {
            await AdMob.resumeBanner();
            setMargin(VISIBLE_MARGIN);
        } catch (err) {
            console.error("[ads] Native resumeBanner failed:", err);
            setMargin(0);
        }
        return;
    }

    try {
        [WEB_IDS.bottom, WEB_IDS.center, WEB_IDS.horizontal, WEB_IDS.vertical, WEB_IDS.square].forEach(
            (id) => {
                const el = document.getElementById(id);
                if (el) el.style.display = "";
            }
        );
        // Unknown which is visible; set safe fallback for mobile
        setMargin(VISIBLE_MARGIN);
    } catch (err) {
        console.error("[ads] Web resumeBanner failed:", err);
        setMargin(0);
    }
}

/** Fully remove banner */
export async function removeBanner() {
    if (IS_NATIVE) {
        try {
            await AdMob.removeBanner();
        } catch (err) {
            console.error("[ads] Native removeBanner failed:", err);
        } finally {
            setMargin(0);
        }
        return;
    }

    try {
        removeWebAd();
    } catch (err) {
        console.error("[ads] Web removeBanner failed:", err);
        setMargin(0);
    }
}
